import {
  ArrowRight,
  BookOpen,
  BookOpenCheck,
  Building2,
  ShieldCheck,
  UsersRound,
} from "lucide-react";

import { useModalFocus } from "../lib/useModalFocus";
import type { GameState } from "../types";
import { EventArtwork } from "./EventArtwork";

interface LegacySecuredDialogProps {
  state: GameState;
  onContinue: () => void;
  onReviewHistory: () => void;
}

export function LegacySecuredDialog({
  state,
  onContinue,
  onReviewHistory,
}: LegacySecuredDialogProps) {
  const outcome = state.campaign_outcome;
  const living = state.survivors.filter((survivor) => survivor.alive).length;
  const sites = outcome?.site_number ?? state.site.number;
  const attacks = state.progress?.attacks_survived_total ?? 0;
  const knowledge = state.research.completed.length;
  const modalRef = useModalFocus<HTMLDivElement>({
    focusKey: `legacy-${outcome?.achieved_at ?? state.site.number}`,
    onEscape: onContinue,
  });

  return (
    <div
      ref={modalRef}
      className="legacy-secured-backdrop modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legacy-secured-title"
      aria-describedby="legacy-secured-summary"
      tabIndex={-1}
    >
      <section
        className="legacy-secured-dialog"
      >
        <div className="legacy-ledger-mark" aria-hidden="true">
          <ShieldCheck />
          <span>Continuity ledger · first cycle complete</span>
        </div>
        <EventArtwork
          variant="bunker_rebuild"
          caption="A future measured in communities, not bunkers"
        />
        <header>
          <span className="eyebrow">A living legacy</span>
          <h2 id="legacy-secured-title">
            {outcome?.title ?? "The community endures"}
          </h2>
          <p id="legacy-secured-summary">
            {outcome?.summary
              ?? "The survivors can sustain a refuge, understand the surface, and carry their knowledge before any one bunker becomes a grave."}
          </p>
        </header>
        <dl className="legacy-record" aria-label="Community legacy record">
          <div>
            <Building2 aria-hidden="true" />
            <dt>Sites held</dt>
            <dd>{sites}</dd>
          </div>
          <div>
            <ShieldCheck aria-hidden="true" />
            <dt>Hordes survived</dt>
            <dd>{attacks}</dd>
          </div>
          <div>
            <BookOpenCheck aria-hidden="true" />
            <dt>Knowledge retained</dt>
            <dd>{knowledge}</dd>
          </div>
          <div>
            <UsersRound aria-hidden="true" />
            <dt>People living</dt>
            <dd>{living}</dd>
          </div>
        </dl>
        <p className="legacy-endless-note">
          <strong>The story does not stop here.</strong>
          This community has secured a future, but supplies, factions, expeditions,
          and the horde continue in endless play.
        </p>
        <footer>
          <button type="button" className="secondary-button" onClick={onReviewHistory}>
            <BookOpen aria-hidden="true" />
            Read the ledger
          </button>
          <button type="button" className="action-button" onClick={onContinue}>
            Continue the community
            <ArrowRight aria-hidden="true" />
          </button>
        </footer>
      </section>
    </div>
  );
}
